import { supabase } from '@/lib/supabase';
import { toUserMessage } from '@/lib/errors';
import { ok, err, type ApiResponse } from './_base';

// ═══════════════════════════════════════════════════════════════════════════
// Búsqueda global (Cmd+K) — devuelve matches en 3 categorías
// ═══════════════════════════════════════════════════════════════════════════

export type SearchResultKind =
  | 'administracion'
  | 'edificio'
  | 'comprobante'
  | 'movimiento';

export interface SearchResult {
  kind: SearchResultKind;
  id: string;
  label: string;
  /** Segunda línea más chica (código, admin, fecha, etc.) */
  sublabel?: string;
  /** Tercera línea opcional (monto, cuit, etc.) */
  extra?: string;
  /** Para el ranking por relevancia. */
  score: number;
}

export interface GlobalSearchPayload {
  administraciones: SearchResult[];
  edificios: SearchResult[];
  comprobantes: SearchResult[];
  movimientos: SearchResult[];
}

const LIMIT_POR_CATEGORIA = 5;

/**
 * Busca por término en admin / edificio / comprobante / movimiento. Retorna
 * agrupado para el modal Cmd+K. La query mínima es 2 caracteres; con menos
 * devuelve vacío.
 */
export async function searchGlobal(
  empresaId: string,
  query: string,
): Promise<ApiResponse<GlobalSearchPayload>> {
  const q = query.trim();
  if (q.length < 2) {
    return ok({
      administraciones: [],
      edificios: [],
      comprobantes: [],
      movimientos: [],
    });
  }

  const termIlike = `%${q.toLowerCase()}%`;

  // Detecta si la query parece un número (útil para comprobantes por nº).
  const soloDigitos = /^\d+$/.test(q);

  const [adminsRes, edifsRes, compsRes, movsRes] = await Promise.all([
    // Administraciones: match por código, nombre o CUIT.
    supabase
      .from('administraciones')
      .select('id, codigo, nombre, cuit, responsable_nombre, activo')
      .eq('empresa_id', empresaId)
      .or(`codigo.ilike.${termIlike},nombre.ilike.${termIlike},cuit.ilike.${termIlike}`)
      .order('activo', { ascending: false })
      .limit(LIMIT_POR_CATEGORIA),

    // Edificios: match por código, nombre, nombre_normalizado o cuit.
    supabase
      .from('edificios')
      .select(
        'id, codigo, nombre, numero_documento, activo, administraciones!inner(id, codigo, nombre)',
      )
      .eq('empresa_id', empresaId)
      .or(
        `codigo.ilike.${termIlike},nombre.ilike.${termIlike},nombre_normalizado.ilike.${termIlike},numero_documento.ilike.${termIlike}`,
      )
      .order('activo', { ascending: false })
      .limit(LIMIT_POR_CATEGORIA),

    // Comprobantes: si son sólo dígitos, busco por número; si no, por razón social.
    soloDigitos
      ? supabase
          .from('comprobantes')
          .select(
            'id, tipo, punto_venta, numero, fecha, total, estado, receptor_razon_social, edificios(nombre), administraciones(codigo, nombre)',
          )
          .eq('empresa_id', empresaId)
          .eq('numero', Number(q))
          .order('fecha', { ascending: false })
          .limit(LIMIT_POR_CATEGORIA)
      : supabase
          .from('comprobantes')
          .select(
            'id, tipo, punto_venta, numero, fecha, total, estado, receptor_razon_social, edificios(nombre), administraciones(codigo, nombre)',
          )
          .eq('empresa_id', empresaId)
          .ilike('receptor_razon_social', termIlike)
          .order('fecha', { ascending: false })
          .limit(LIMIT_POR_CATEGORIA),

    // Movimientos: match por descripción o referencia. Útil para encontrar
    // rápido un movimiento bancario por texto.
    supabase
      .from('movimientos')
      .select('id, fecha, tipo, monto, descripcion, referencia, caja_id, cajas(nombre)')
      .eq('empresa_id', empresaId)
      .or(`descripcion.ilike.${termIlike},referencia.ilike.${termIlike}`)
      .order('fecha', { ascending: false })
      .limit(LIMIT_POR_CATEGORIA),
  ]);

  if (adminsRes.error) return err('SEARCH_ADMIN', toUserMessage(adminsRes.error));
  if (edifsRes.error) return err('SEARCH_EDIF', toUserMessage(edifsRes.error));
  if (compsRes.error) return err('SEARCH_COMP', toUserMessage(compsRes.error));
  if (movsRes.error) return err('SEARCH_MOV', toUserMessage(movsRes.error));

  const administraciones: SearchResult[] = (adminsRes.data ?? []).map((a) => ({
    kind: 'administracion',
    id: a.id,
    label: a.nombre,
    sublabel: `Cód. ${a.codigo}${a.responsable_nombre ? ' · ' + a.responsable_nombre : ''}`,
    extra: a.cuit ? `CUIT ${a.cuit}` : undefined,
    score: scoreMatch(q, [a.codigo, a.nombre, a.cuit, a.responsable_nombre]),
  }));

  const edificios: SearchResult[] = (edifsRes.data ?? []).map((e) => {
    const adm = (e as Record<string, unknown>).administraciones as
      | { id: string; codigo: string; nombre: string }
      | undefined;
    return {
      kind: 'edificio',
      id: e.id,
      label: e.nombre,
      sublabel: adm
        ? `Cód. ${e.codigo} · Adm ${adm.codigo} ${adm.nombre}`
        : `Cód. ${e.codigo}`,
      extra: e.numero_documento ? `Doc ${e.numero_documento}` : undefined,
      score: scoreMatch(q, [e.codigo, e.nombre, e.numero_documento]),
    };
  });

  const fmtNum = (pv: number, n: number | null) =>
    n === null ? 'S/N' : `${String(pv).padStart(5, '0')}-${String(n).padStart(8, '0')}`;

  const comprobantes: SearchResult[] = (compsRes.data ?? []).map((c) => {
    const edif = (c as Record<string, unknown>).edificios as
      | { nombre: string }
      | undefined;
    const adm = (c as Record<string, unknown>).administraciones as
      | { codigo: string; nombre: string }
      | undefined;
    return {
      kind: 'comprobante',
      id: c.id,
      label: `${c.tipo} ${fmtNum(c.punto_venta, c.numero)}`,
      sublabel: `${edif?.nombre ?? c.receptor_razon_social}${adm ? ' · ' + adm.nombre : ''}`,
      extra: `${c.fecha} · $ ${Number(c.total).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`,
      score: scoreMatch(q, [
        String(c.numero),
        c.receptor_razon_social,
        edif?.nombre ?? null,
      ]),
    };
  });

  const movimientos: SearchResult[] = (movsRes.data ?? []).map((m) => {
    const caja = (m as Record<string, unknown>).cajas as { nombre: string } | undefined;
    return {
      kind: 'movimiento',
      id: m.id,
      label: (m.descripcion ?? '(sin descripción)').slice(0, 80),
      sublabel: `${caja?.nombre ?? ''} · ${m.fecha} · ${m.tipo}`,
      extra: `$ ${Number(m.monto).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`,
      score: scoreMatch(q, [m.descripcion, m.referencia]),
    };
  });

  // Ordena cada categoría por score DESC.
  [administraciones, edificios, comprobantes, movimientos].forEach((arr) =>
    arr.sort((a, b) => b.score - a.score),
  );

  return ok({ administraciones, edificios, comprobantes, movimientos });
}

/**
 * Score simple para ordenar resultados: +2 si la query aparece al inicio,
 * +1 si aparece como palabra completa, +0.5 si aparece como substring.
 * Normalizada a lowercase.
 */
function scoreMatch(query: string, fields: (string | null | undefined)[]): number {
  const q = query.toLowerCase();
  let score = 0;
  for (const f of fields) {
    if (!f) continue;
    const s = f.toLowerCase();
    if (s.startsWith(q)) score += 2;
    else if (s.includes(' ' + q) || s.endsWith(' ' + q)) score += 1;
    else if (s.includes(q)) score += 0.5;
  }
  return score;
}
